import { Injectable, Injector } from "@angular/core";
import { Connection } from "@common/ADAPT.Common.Model/organisation/connection";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { CulturalExclusion } from "@common/ADAPT.Common.Model/organisation/cultural-exclusion";
import { CulturalIndex, CulturalIndexBreezeModel } from "@common/ADAPT.Common.Model/organisation/cultural-index";
import { CulturalRelationship } from "@common/ADAPT.Common.Model/organisation/cultural-relationship";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { BreezeService } from "@common/lib/data/breeze.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { CulturalActivityService } from "@org-common/lib/cultural-activity/cultural-activity.service";
import { CulturalLeadershipAccessLevels } from "@org-common/lib/cultural-leadership/cultural-leadership-access-levels.enum";
import { CulturalLeadershipFrameworkAuthService } from "@org-common/lib/cultural-leadership/cultural-leadership-framework-auth.service";
import { ICulturalLeadershipPersonalAnalysisData } from "app/features/culture/cultural-leadership/cultural-leadership-personal-analysis-data.interface";
import moment from "moment";
import { lastValueFrom, merge } from "rxjs";
import { CulturalLeadershipAnalysisDataBaseService } from "../../culture/cultural-leadership/cultural-leadership-analysis-data-base.service";
import { ICulturalLeadershipMeasurementAnalysisFilter } from "../../culture/cultural-leadership/cultural-leadership-measurement-analysis-filter.interface";
import { CulturalIndexAnalysisFilter } from "./cultural-index-analysis-filter";
import { CulturalIndexDataService } from "./cultural-index-data.service";

@Injectable({
    providedIn: "root",
})
@Autobind
export class CulturalIndexAnalysisDataService extends CulturalLeadershipAnalysisDataBaseService {
    private readonly partialCulturalIndexCache = new Map<string, Promise<CulturalIndex[]>>();

    protected primeModels = [CulturalIndexBreezeModel];

    public constructor(
        injector: Injector,
        private culturalIndexDataService: CulturalIndexDataService,
        private culturalLeadershipFrameworkAuthService: CulturalLeadershipFrameworkAuthService,
        private culturalActivityService: CulturalActivityService,
        breezeService: BreezeService,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super(injector);

        merge(
            breezeService.dataCacheCleared$,
            rxjsBreezeService.entityTypeChangedInSave(CulturalIndex),
        )
            .subscribe(() => this.partialCulturalIndexCache.clear());
    }

    public async promiseToGetCulturalIndexAnalysisData(
        initialFilter?: ICulturalLeadershipMeasurementAnalysisFilter,
        prefetchAllData = false,
        includeExcludedPeople = false,
    ) {
        const queryOptions = {
            namedParams: {
                latestOnly: true,
            },
        };

        const filter = initialFilter ? initialFilter : CulturalIndexAnalysisFilter.getDefaultFilterParameters();
        const date = filter.date ? filter.date : new Date(); // data to filter/limit all records is now or the passed in date

        try {
            const accessLevel = await this.culturalIndexDataService.promiseToCheckCulturalIndexAccessLevel();
            const hasQuantitativeAccess = accessLevel === CulturalLeadershipAccessLevels.QuantitativeAccess || accessLevel === CulturalLeadershipAccessLevels.FullAccess;
            const isCulturalLeader = await this.culturalLeadershipFrameworkAuthService.currentPersonIsCulturalLeader();
            const [connections, culturalRelationships, culturalExclusions] = await this.getPrimeData(queryOptions, filter, accessLevel, prefetchAllData);
            const culturalIndexes = await this.getCulturalIndexData(queryOptions, filter, hasQuantitativeAccess);
            const culturalActivities = hasQuantitativeAccess || isCulturalLeader
                ? await lastValueFrom(this.culturalActivityService.getCulturalActivitiesForActivePeople([CulturalActivityType.CulturalIndex]))
                : [];
            const culturalData = this.convertCulturalIndexDataToAnalysisData(culturalIndexes, date, culturalActivities, culturalExclusions);
            const processedCulturalIndexes = this.processCulturalIndexAnalysisData(culturalData, connections, hasQuantitativeAccess, date, filter, culturalRelationships, culturalExclusions, culturalActivities);
            return includeExcludedPeople
                ? processedCulturalIndexes
                : processedCulturalIndexes.filter((data) => !data.personIsExcluded);
        } catch (error) {
            this.log.warn(error as string);
            return Promise.reject(error);
        }
    }

    private async getCulturalIndexData(queryOptions: any, filter: ICulturalLeadershipMeasurementAnalysisFilter, hasQuantitativeAccess: boolean) {
        let key = "latestCulturalIndexesByDate";

        if (!hasQuantitativeAccess) {
            queryOptions.namedParams.activeOnly = true;
        }

        if (filter.date) {
            queryOptions.namedParams.date = filter.date;
            key += filter.date.toDateString();
        } else {
            key += "Today";
        }

        if (queryOptions.isPartial) {
            return this.getCachedPartialData(key, () => lastValueFrom(
                this.commonDataService.getWithOptions(CulturalIndexBreezeModel, key, {
                    ...queryOptions,
                    namedParams: { ...queryOptions.namedParams },
                    forceRemote: true,
                })));
        }

        return lastValueFrom(this.commonDataService.getWithOptions(CulturalIndexBreezeModel, key, queryOptions));
    }

    private getCachedPartialData(key: string, getData: () => Promise<CulturalIndex[]>) {
        const cachedData = this.partialCulturalIndexCache.get(key);
        if (cachedData) {
            return cachedData;
        }

        const request = getData().catch((error) => {
            this.partialCulturalIndexCache.delete(key);
            return Promise.reject(error);
        });
        this.partialCulturalIndexCache.set(key, request);
        return request;
    }

    private convertCulturalIndexDataToAnalysisData(culturalIndexes: CulturalIndex[], date: Date, culturalActivities: CulturalActivity[], culturalExclusions: CulturalExclusion[]) {
        const excludedPersonIds = new Set(culturalExclusions.map((ce) => ce.personId));
        return culturalIndexes.map((ci) => this.mapCulturalIndex(ci, date, culturalActivities.filter((event) => event.person1Id === ci.personId), excludedPersonIds.has(ci.personId)));
    }

    private mapCulturalIndex(culturalIndex: CulturalIndex, date: Date, culturalActivities: CulturalActivity[], personIsExcluded = false) {
        const catchupData = this.culturalLeadershipFrameworkService.getIndividualCohortMemberStatus(culturalActivities, undefined, undefined, undefined, culturalIndex);

        const data = {
            person: culturalIndex.person,
            personIsExcluded,
            culturalIndex,
            latestCatchup: culturalIndex.creationDate,
            nextCatchup: catchupData.nextCI,
        } as ICulturalLeadershipPersonalAnalysisData;

        data.employeeConnections = data.person
            ? data.person.connections.filter((connection: Connection) => connection.isEmployeeConnection())
            : [];
        data.catchupStatus = this.culturalLeadershipFrameworkService.getCatchupStatusFromDate(date, data.nextCatchup);

        return data;
    }

    private processCulturalIndexAnalysisData(culturalData: ICulturalLeadershipPersonalAnalysisData[], connections: Connection[], hasQuantitativeAccess: boolean, date: Date, filter: any, culturalRelationships: CulturalRelationship[], culturalExclusions: CulturalExclusion[], culturalActivities: CulturalActivity[]) {
        culturalData = this.filterByCulturalMeasurement(culturalData, connections, hasQuantitativeAccess, date, filter, culturalRelationships, culturalExclusions, culturalActivities);

        // show all trends; return all records
        if (filter.showTrendOutwardsOnly) {
            culturalData = culturalData.filter((data) => this.trendFilter(data, date));
        }

        return culturalData;
    }

    private trendFilter(analysisData: ICulturalLeadershipPersonalAnalysisData, date: Date) {
        if (!analysisData.culturalIndex || !analysisData.person) {
            return false;
        }

        // not enough data to work this out
        if (analysisData.person.culturalIndexes.length < 3) {
            return false;
        }

        const valueCulturalIndexes = analysisData.person.culturalIndexes
            .filter((ci: CulturalIndex) => moment(ci.creationDate).isBefore(date))
            .sort((a: CulturalIndex, b: CulturalIndex) => moment(a.creationDate) < moment(b.creationDate) ? 1 : -1);

        // not enough data to work this out
        if (valueCulturalIndexes.length < 3) {
            return false;
        }

        return (valueCulturalIndexes[0].value - valueCulturalIndexes[2].value) > 0.25;
    }
}
